import { Fragment, useEffect, useRef, useState } from 'react';
import { kindLabel } from '../lib/driverKind';
import { api, CONTACT_KIND_HINT, CONTACT_KIND_LABEL, externalDupConfirm, externalDupDone, uploadFileAsset, type CustContact, type CustDupHit, type ExternalDup, type FileOwnerType, type MasterKind } from '../lib/api';
import { ErrorBox, type Row } from '../components/DataTable';
import FileAssets from '../components/FileAssets';
import AllocCarryPanel from '../components/AllocCarryPanel';
import AllocHistoryPanel from '../components/AllocHistoryPanel';
import CarChangePanel from '../components/CarChangePanel';
import { can, canScreen, useMe } from '../lib/me';
import { useAutoSearch } from '../lib/search';
import { Link } from 'react-router-dom';

/**
 * 서류(이미지)가 붙는 탭. 레거시 blob 컬럼이 있던 표만이다 —
 * 차주·차고지·편성은 레거시에도 이미지가 없다(`db/schema/_file_asset.sql` 실측표).
 */
const FILE_OWNER: Partial<Record<MasterKind, FileOwnerType>> = {
  driver: 'driver', car: 'car', cust: 'cust', grp: 'grp',
};

/**
 * 기준정보 등록·수정.
 *
 * 탭마다 입력 항목이 달라서 **필드 정의를 설정으로** 둔다 — 화면을 6벌 만들면
 * 한 곳만 고치고 나머지를 빠뜨리게 된다.
 *
 * ⚠️ 기사·차주는 저장하면 **로그인 ID 가 자동 생성**된다(기사 D+연락처 / 차주 O+휴대폰).
 *    레거시 기사앱이 이 규칙으로 로그인하기 때문이다. 초기 비밀번호는 연락처 뒤 4자리 —
 *    평문이라 전환 시점에 전면 재설정해야 한다. 그 사실을 화면에 적어 둔다.
 */

/** 기사 폼에서 「급여 지급 설정」을 끼워 넣을 자리 — 급여·급여구분·고용형태·체크박스 다음, 주소·메모 앞. */
const PAY_AFTER = 'isBidding';

interface FieldDef {
  key: string;
  label: string;
  /** dateText = 비었거나 YYYY-MM-DD 면 달력, 옛 자료의 다른 형식이면 글자 칸 그대로(저장 때 안 지워지게). */
  type?: 'text' | 'number' | 'date' | 'dateText' | 'select' | 'checkbox' | 'ref' | 'textarea' | 'money';
  /** type='ref' 일 때 무엇을 고르는지. 숫자 ID 를 손으로 치게 두면 반드시 틀린다. */
  ref?: 'grp' | 'owner' | 'garage' | 'car' | 'driver';
  options?: string[];
  required?: boolean;
  hint?: string;
  /** 한 줄을 통째로 쓴다 — 메모·이력처럼 긴 값은 반 칸에 넣으면 읽히지 않는다. */
  wide?: boolean;
  /** type='ref' 에서 수정 화면의 "현재 선택" 이름을 읽을 목록 컬럼 — 같은 참조가 한 폼에 두 번 나올 때(차주·번호판 권리). */
  labelCol?: string;
  /** 차주 「내부/외부」가 외부일 때 칸에 흐리게 보이는 입력 양식(09-29 way 「외부를 고르면 외부 양식을 보여 주면」). */
  extPh?: string;
  /** 12칸 중 차지하는 폭(기본 4 = 한 줄 셋). wide 는 12. */
  span?: 2 | 3 | 4 | 6 | 8 | 12;
  /**
   * 화면에서 뺀 칸(#139 번호판 지역·형식코드). **정의는 남긴다** — 저장이 칸 전체를 보내므로
   * 정의째 지우면 그 값이 빈칸으로 덮인다(레거시 버그 #3 부류). 읽은 값을 그대로 돌려보낸다.
   */
  hidden?: boolean;
  /**
   * 외부 소속이면 숨긴다(10-29 way 「외부면 입력 필요 없는 칸은 안 보이게」) — true = 외부 전부 · '개인'/'관광사' = 그 쪽만.
   * 근거 = 운행 중 외부 기사·차량 2,176·2,144 의 채움률(대부분 0~16% · 나머지는 레거시 기본값). **정의·값은 남는다**(저장 때 그대로 돌려보냄) ·
   * 「숨긴 칸 보기」로 펼친다.
   */
  extHide?: true | '개인' | '관광사';
  /** 외부 양식이면 필수 아님(#523 · 외부 편성 시작일 — 서버 `saveCarAlloc` 과 같은 판정). */
  extOptional?: true;
}

/** 외부 양식 — 차주는 내부/외부·개인 칸, 차량은 고른 소속, 기사는 운행 중 편성(서버 `ext_kind`). */
type ExtKind = '개인' | '관광사' | '외부' | null;

/**
 * 배치(#139·#140) — 칸을 묶음별로 그린다. `files` = 서류 칸 자리 · `side` = 넓은 화면에서 오른쪽 열.
 * 묶음에 안 적힌 칸은 마지막 본문 묶음 뒤에 붙는다(새 칸을 빠뜨려도 화면에서 사라지지 않게).
 * `_rrn` = 주민번호 칸 자리(기사).
 */
interface Section { title?: string; keys?: string[]; files?: boolean; side?: boolean; note?: string }

const SPAN: Record<number, string> = {
  2: 'md:col-span-2', 3: 'md:col-span-3', 4: 'md:col-span-4', 6: 'md:col-span-6', 8: 'md:col-span-8', 12: 'md:col-span-12',
};

/** 금액 칸 — 소수 0 을 떼고 천 단위 쉼표(#139). 저장은 숫자 글자만. */
const moneyView = (v: unknown) => {
  const raw = String(v ?? '').replace(/,/g, '').trim();
  if (raw === '') return '';
  const [i, f] = raw.split('.');
  const frac = (f ?? '').replace(/0+$/, '');
  const n = Number(i);
  return (Number.isFinite(n) ? n.toLocaleString('ko-KR') : i) + (frac ? `.${frac}` : '');
};

const FIELDS: Record<MasterKind, { title: string; pk: string; fields: FieldDef[]; note?: string; sections?: Section[] }> = {
  grp: {
    title: '소속',
    pk: 'grpId',
    note: '소속명은 배차·권한이 이름으로 연결되어 있습니다. 바꾸면 그 연결이 끊어질 수 있습니다.',
    fields: [
      { key: 'grpName', label: '소속명', required: true },
      { key: 'grpKind', label: '구분', type: 'select', options: ['', '내부', '외부'] },
      { key: 'corpName', label: '법인명' },
      { key: 'bizNumber', label: '사업자번호' },
      { key: 'master', label: '대표자' },
      { key: 'manager', label: '담당자' },
      { key: 'phone', label: '연락처' },
      { key: 'addr', label: '주소' },
      { key: 'isHidden', label: '숨김(사용 안 함)', type: 'checkbox', hint: '켜면 기준정보 목록(「사용중만」)과 신규 접수 배차 소속에서 빠집니다 · 지난 접수·편성의 소속 표시는 그대로' },
    ],
  },
  cust: {
    title: '거래처',
    pk: 'custId',
    fields: [
      { key: 'custName', label: '거래처명', required: true },
      { key: 'custCode', label: '코드' },
      { key: 'custKind', label: '구분', type: 'select', options: ['', '일반', '장기'] },
      // 10-08 way 「필수값」 — 서버는 새 거래처에만 강제하고 기존 거래처는 있던 값을 지우는 것만 막는다.
      { key: 'bizNumber', label: '사업자번호', required: true },
      { key: 'phone', label: '연락처', required: true },
      { key: 'fax', label: '팩스' },
      { key: 'address', label: '주소', span: 12 },
      // 담당자·정산담당자는 여러 명(10-08 way) — `_contacts` 칸이 목록으로 받는다. 기본 칸은 정의만 남긴다(종류별 첫 줄이 서버에서 그대로 적힌다).
      { key: '_contacts', label: '거래처담당자 · 거래처정산담당자', wide: true },
      { key: 'manager', label: '거래처담당자', hidden: true },
      { key: 'managerPhone', label: '거래처담당자 연락처', hidden: true },
      { key: 'managerEmail', label: '거래처담당자 이메일', hidden: true },
      { key: 'custFinManager', label: '거래처정산담당자', hidden: true },
      { key: 'custFinPhone', label: '정산 연락처', hidden: true },
      { key: 'bizStatus', label: '업태' },
      { key: 'bizKind', label: '업종' },
      { key: 'driveType', label: '운행구분', hint: '일반 / 출퇴근 / 셔틀 등' },
      { key: 'manageType', label: '관리구분', hint: 'A(관리대상) / N(비관리대상)' },
      { key: 'callCenter', label: '콜센터' },
      { key: 'isDeposit', label: '선입금', type: 'checkbox' },
      { key: 'isSms', label: 'SMS 수신', type: 'checkbox' },
      { key: 'comment', label: '메모', type: 'textarea', wide: true },
      // ⚠️ 통근(buslink) 설정(web_*·reserv_*·use_nfc…)은 **일부러 안 넣는다** — SCOPE 밖이고
      //    여기서 고치면 buslink 와 값이 갈린다.
    ],
  },
  owner: {
    title: '차주',
    pk: 'ownerId',
    note: '저장하면 로그인 ID 가 O+휴대폰으로 자동 생성됩니다(차주앱 규칙).',
    fields: [
      { key: 'ownerName', label: '차주명', required: true, extPh: '관광사명·개인 이름 그대로(예: 제로쿨투어 · 홍길동) — 개인_·관광사_ 는 붙이지 않음' },
      { key: 'mobilePhone', label: '휴대전화', required: true, hint: '로그인 ID 가 이 번호로 만들어집니다', extPh: '010-0000-0000' },
      { key: 'phone', label: '전화', extPh: '031-000-0000 (사무실)' },
      { key: 'belong', label: '내부/외부', type: 'select', options: ['', '내부', '외부'], hint: '새 차주는 필수' },
      { key: 'bizNumber', label: '사업자번호', hint: '외부 관광사 필수(청구)', extPh: '000-00-00000', extHide: '개인' },
      { key: 'bankName', label: '은행', extPh: '국민 · 신한 · 농협 …' },
      { key: 'bank', label: '계좌', hint: '외부 개인 필수(3.3% 지급)', extPh: '000-000-000000 (예금주가 다르면 뒤에 예금주)' },
      { key: 'virtualBank', label: '가상계좌', extHide: true },
      { key: 'email', label: '이메일', hint: '외부 관광사 필수(청구)', extPh: '세금계산서 받을 이메일', extHide: '개인' },
      { key: 'ownerUse', label: '사용구분', hint: '레거시 코드(1/2)', extHide: true },
      { key: 'isPrivate', label: '개인', type: 'checkbox', hint: '외부 기사 본인이 차주(3.3%) · 끄면 관광사' },
      { key: 'isOther', label: '타사', type: 'checkbox', extHide: '개인' },
      // ⚠️ 정산 조건이 여기 적혀 있다(3.3% 원천징수 · 세금계산서 발행 등). 돈이 걸린 칸이다.
      { key: 'comment', label: '정산 메모', type: 'textarea', wide: true, extPh: '정산 조건 — 예: 세금계산서 발행 / 개인 3.3% 원천징수 / 지급일' },
      // ⚠️ is_direct(직영 여부)는 **넣지 않는다** — SPEC §3 이 "믿지 않는다"고 못박은 사문화
      //    컬럼이다(활성 2,932건 중 true 8건). 직영/지입 판별의 정본은 기사의 '기사구분' 하나뿐이다.
    ],
  },
  driver: {
    title: '기사',
    pk: 'driverId',
    note: '저장하면 로그인 ID 가 D+연락처로 자동 생성됩니다(기사앱 규칙). 초기 비밀번호는 뒤 4자리입니다.',
    fields: [
      { key: 'driverName', label: '기사명', required: true, hint: '외부는 개인_차주이름 · 관광사_관광사명' },
      { key: 'phone', label: '연락처', required: true, hint: '로그인 ID 가 이 번호로 만들어집니다' },
      {
        key: 'driverKind',
        label: '기사구분',
        type: 'select',
        required: true,
        options: ['', '차주소유기사', '직영사업장기사', '직영기사'],
        hint: '직영/지입 판별의 기준입니다',
      },
      { key: 'licKind', label: '면허종류' },
      { key: 'dateDriveLic', extHide: true, label: '운전면허일', type: 'date' },
      { key: 'dateBusLic', extHide: true, label: '버스자격일', type: 'date' },
      { key: 'joinDate', extHide: true, label: '입사일', type: 'date' },
      { key: 'bankName', extHide: true, label: '은행' },
      { key: 'bankOwner', extHide: true, label: '예금주' },
      { key: 'bankNumber', extHide: true, label: '계좌번호' },
      { key: 'isResign', label: '퇴사', type: 'checkbox' },
      { key: 'resignDate', label: '퇴사일', type: 'date' },

      // 2026-08-02 추가 — 레거시 Driver 에 있는데 화면에 없던 칸들.
      { key: 'licNumber', extHide: true, label: '면허번호' },
      { key: 'busLicNo', extHide: true, label: '버스자격증번호' },
      { key: 'dateCareer', extHide: true, label: '경력증일', type: 'date' },
      { key: 'dateDriveTest', extHide: true, label: '정밀검사일', type: 'date' },
      { key: 'dateBosuEdu', extHide: true, label: '보수교육일', type: 'date' },
      { key: 'bosuArea', extHide: true, label: '보수교육 지역' },
      { key: 'phone2', label: '연락처2' },
      { key: 'phone3', extHide: true, label: '연락처3' },
      { key: 'carNumberArea', extHide: true, label: '차량번호 지역', hint: '외부 / 경기 / 서울 / 직원 등' },
      { key: 'manageType', extHide: true, label: '관리구분', hint: 'A(관리대상) / N(비관리대상)' },
      { key: 'salary', extHide: true, label: '급여', type: 'number' },
      { key: 'salaryKind', extHide: true, label: '급여구분', hint: '레거시 코드(0/1/2) — 뜻이 문서화돼 있지 않습니다' },
      { key: 'empType', extHide: true, label: '고용형태', hint: '정규직 등' },
      { key: 'isEmployee', extHide: true, label: '직원', type: 'checkbox' },
      { key: 'isMessage', extHide: true, label: '문자 수신', type: 'checkbox' },
      { key: 'isBidding', label: '입찰 참여', type: 'checkbox' },
      { key: 'addr', extHide: true, label: '주소', wide: true },
      { key: 'comment', label: '메모', type: 'textarea', wide: true },
      { key: 'comment1', extHide: true, label: '상담메모1', type: 'textarea', wide: true },
      { key: 'comment2', extHide: true, label: '상담메모2', type: 'textarea', wide: true },
      // 🔒 주민번호·비밀번호·로그인ID 는 **폼에 두지 않는다.** 들어올 자리를 아예 만들지 않는다.
    ],
    // 구 버스인 기사등록 배치(#140) — 개인정보 / 서류(+갱신일) / 계좌 · 오른쪽 기타사항.
    sections: [
      { title: '개인정보', keys: ['driverName', 'phone', 'driverKind', 'joinDate', 'isResign', 'resignDate',
        'phone2', 'phone3', 'carNumberArea', 'addr', '_rrn', 'licNumber', 'licKind', 'busLicNo', 'manageType',
        'salary', 'salaryKind', 'empType', 'isEmployee', 'isMessage', 'isBidding'] },
      { title: '서류 · 갱신일', files: true, keys: ['dateDriveLic', 'dateBusLic', 'dateCareer', 'dateDriveTest', 'dateBosuEdu', 'bosuArea'] },
      { title: '계좌', keys: ['bankName', 'bankOwner', 'bankNumber'] },
      { title: '기타사항', side: true, keys: ['comment', 'comment1', 'comment2'] },
    ],
  },
  car: {
    title: '차량',
    pk: 'carId',
    note: '운용중인 차량과 같은 차량번호는 새로 등록하거나 그 번호로 바꿀 수 없습니다 — 번호판을 옮기기 전 새 차는 「운용중」을 끄고 미리 넣어 두었다가, 옛 차 번호를 바꾼 뒤 켜세요.',
    fields: [
      { key: 'carNo', label: '차량번호', required: true, hint: '외부는 개인_차주이름 · 관광사_관광사명' },
      { key: 'carName', extHide: true, label: '차량명' },
      { key: 'grpId', label: '소속', type: 'ref', ref: 'grp', required: true },
      { key: 'ownerId', label: '차주', type: 'ref', ref: 'owner' },
      { key: 'garageId', label: '차고지', type: 'ref', ref: 'garage' },
      { key: 'fullCnt', label: '정원', type: 'number', span: 2 },
      { key: 'carYear', label: '연식', type: 'number', span: 2 },
      { key: 'carTypeGubun', extHide: true, label: '유종·규격' },
      { key: 'insExpire', label: '보험만기', type: 'date' },
      { key: 'insValid', label: '보험유효기간', type: 'date' },
      { key: 'testExpire', label: '검사만기', type: 'date' },

      // ── 자동차등록증(2026-08-02 추가) ──────────────────────────────
      // 레거시 Car 에 있던 값인데 화면에 없어 **차대번호·형식을 웹에서 볼 수도 고칠 수도 없었다.**
      { key: 'identNo', extHide: true, label: '차대번호', hint: '보통 17자 — 길이를 강제하지 않습니다' },
      { key: 'carType', extHide: true, label: '형식', hint: '예: HD2-442-16PaZ6-BEIT' },
      { key: 'carCode', extHide: true, label: '형식코드', hint: '「조회」로 고르면 차량명이 채워집니다' },
      // ⚠️ **date 로 두지 말 것.** 옛 자료에 3/29/2019 같은 형식이 섞여 있어(실측 3,267·1,080건)
      //    날짜 입력이 그 값을 못 읽고, 저장하면 통째로 지워진다.
      { key: 'carDate', extHide: true, label: '최초등록일', type: 'dateText', hint: '넣으면 검사만기(1년 뒤 −1일)·차령만료일이 채워집니다' },
      { key: 'carExpire', label: '차령만료일', hint: '최초등록일을 넣으면 11년 뒤로 채워집니다' },
      { key: 'carNumberArea', label: '번호판 지역', hint: '외부 / 경기 / 서울 / 부산 / 직원', hidden: true },
      { key: 'paint', extHide: true, label: '도색' },
      // GPS 단말 번호(#60) — 레거시 「GPS번호」. 기록용이고 위치 조회와는 무관하다.
      { key: 'gps', extHide: true, label: 'GPS번호', hint: '예: 012-2062-8701' },

      // ── 자산 ──────────────────────────────────────────────────────
      { key: 'carCost', extHide: true, label: '차량가액', type: 'money' },
      // 번호판 소유·명의 두 칸 → 「번호권리」 한 칸(#53). 명의 글자는 서버가 고른 차주 이름으로 채운다.
      // labelCol 필수 — 없으면 refLabelCol('owner') = 차주 이름이 이 칸에 뜬다.
      { key: 'numberOwnerId', extHide: true, label: '번호권리', type: 'ref', ref: 'owner', labelCol: 'number_owner_name' },
      { key: 'numberPrice', extHide: true, label: '번호판 가액', type: 'money' },

      // ── 운용 ──────────────────────────────────────────────────────
      { key: 'isUse', label: '운용중', type: 'checkbox' },
      { key: 'unUseKind', label: '미운용 사유', hint: '매각 / 폐차 등' },
      { key: 'unUseDate', label: '미운용일', type: 'date' },
      { key: 'unUseComment', label: '미운용 메모', span: 8 },
      { key: 'history', extHide: true, label: '이력', type: 'textarea', wide: true },
    ],
    // 차량팀 배치(#139) — 서류를 맨 위(재발급·갱신이 잦다) · 연식/정원 좁게 + GPS 같은 줄.
    sections: [
      { files: true },
      { keys: ['ownerId', 'grpId', 'numberOwnerId', 'carNo', 'identNo', 'carType', 'carCode', 'carName', 'carYear', 'fullCnt', 'gps',
        'carDate', 'testExpire', 'carExpire', 'insValid', 'insExpire', 'carTypeGubun', 'garageId', 'carCost', 'numberPrice',
        'paint', 'isUse', 'unUseDate', 'unUseKind', 'unUseComment', 'history'] },
    ],
  },
  car_alloc: {
    title: '운행차량편성',
    pk: 'carAllocId',
    note: '편성은 배차·정산의 기준입니다. 배차가 나간 뒤에는 차량·차주·소속·기사를 바꿀 수 없습니다 — 새 편성을 만드세요. 외부(관광사·개인) 편성은 목록 위 「외부 편성 등록」으로 만드세요(이름이 양식대로 맞춰집니다).',
    fields: [
      { key: 'carId', label: '차량', type: 'ref', ref: 'car', required: true },
      { key: 'driverId', label: '기사', type: 'ref', ref: 'driver' },
      { key: 'ownerId', label: '차주', type: 'ref', ref: 'owner' },
      { key: 'grpId', label: '소속', type: 'ref', ref: 'grp', required: true },
      { key: 'startdt', label: '시작일', type: 'date', required: true, extOptional: true },
      { key: 'enddt', label: '종료일', type: 'date', hint: '비우면 무기한' },
      { key: 'isUse', label: '정산 진행중', type: 'checkbox', hint: '사용 여부가 아니라 정산 진행 여부입니다' },
      { key: 'fuelCorp', label: '주유카드사' },
      { key: 'fuelNum', label: '주유카드 번호' },
      { key: 'hipassCorp', label: '하이패스사' },
      { key: 'hipassNum', label: '하이패스 번호' },
      { key: 'comment', label: '비고' },
    ],
  },
  garage: {
    title: '차고지',
    pk: 'garageId',
    fields: [
      { key: 'garageName', label: '차고지명', required: true },
      { key: 'addr', label: '주소' },
      { key: 'lat', label: '위도' },
      { key: 'lng', label: '경도' },
    ],
  },
};

/** 편성 카드 칸 — 회계 입력 권한은 기준정보 조회여도 이것만 고친다(#335 · 서버 `saveCarAllocCards`). */
const CARD_KEYS = ['fuelCorp', 'fuelNum', 'hipassCorp', 'hipassNum'];
const BANK_KEYS = ['bankName', 'bankOwner', 'bankNumber'];

const SAVERS: Record<MasterKind, (d: Record<string, unknown>) => Promise<unknown>> = {
  grp: (d) => api.saveGrp(d),
  cust: (d) => api.saveCust(d),
  owner: (d) => api.saveOwner(d),
  driver: (d) => api.saveDriver(d),
  car: (d) => api.saveCar(d),
  garage: (d) => api.saveGarage(d),
  car_alloc: (d) => api.saveCarAlloc(d),
};

const snake = (s: string) => s.replace(/[A-Z]/g, (m) => '_' + m.toLowerCase());

/** 목록 행에서 그 참조의 표시 이름이 들어 있는 컬럼. 수정 화면에서 "현재 선택"을 보여주려고. */
const refLabelCol = (ref: NonNullable<FieldDef['ref']>) =>
  ({ grp: 'grp_name', owner: 'owner_name', garage: 'garage_name', car: 'car_no', driver: 'driver_name' })[ref];

/**
 * 참조 고르기 — 소속·차주·차고지·차량·기사.
 *
 * ⚠️ 숫자 ID 를 손으로 치게 두면 반드시 틀린다(그리고 틀려도 저장은 된다).
 *    검색해서 고르게 하고, 고른 것의 **이름을 보여준다.**
 */
export function RefPicker({
  kind,
  value,
  label,
  onPick,
}: {
  kind: NonNullable<FieldDef['ref']>;
  value: number | null;
  label?: string;
  onPick: (id: number | null) => void;
}) {
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<Row[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState<string>(label ?? '');

  const LOADERS = {
    grp: () => api.listGrps({ q: term.trim() || undefined, limit: 20 }),
    owner: () => api.listOwners({ q: term.trim() || undefined, limit: 20 }),
    garage: () => api.listGarages({ q: term.trim() || undefined, limit: 20 }),
    car: () => api.listCars({ q: term.trim() || undefined, limit: 20, scope: 'active' }),
    driver: () => api.listDrivers({ q: term.trim() || undefined, limit: 20, scope: 'active' }),
  } as const;

  const idCol = `${kind === 'car' ? 'car' : kind}_id`;
  const nameCol = refLabelCol(kind);

  // 고른 뒤에 도착한 조회 응답은 버린다 — 그게 목록을 다시 열어 고른 값이 사라진 것처럼 보였다(게시판 #58).
  const seq = useRef(0);

  /** 실제 조회. 버튼·Enter 와 자동검색이 같은 경로를 쓴다. */
  async function run() {
    const my = ++seq.current;
    setBusy(true);
    try {
      const r = await LOADERS[kind]();
      if (my !== seq.current) return;
      let rows = r.rows as unknown as Row[];
      // #289 — 같은 이름의 차주가 여럿이면(동영(용인) 3명) 「사용」(owner_use 1)인 것만 남긴다. 이름이 하나뿐이면 그대로.
      if (kind === 'owner') {
        const used = new Set(rows.filter((x) => Number(x.owner_use) !== 2).map((x) => String(x.owner_name)));
        rows = rows.filter((x) => Number(x.owner_use) !== 2 || !used.has(String(x.owner_name)));
      }
      setHits(rows);
      setOpen(true);
    } finally {
      setBusy(false);
    }
  }

  function search(e: React.FormEvent) {
    e.preventDefault();
    void run();
  }

  // 치는 대로 찾는다(입력이 멎으면 적용). LOADERS 가 term 을 직접 읽으므로 인자를 넘기지 않는다 —
  // 훅이 쏘는 시점의 term 이 곧 최신값이다.
  // ⚠️ 고르는 중(값 없음·「변경」 누름)일 때만 — 고르면 검색어를 비우는데, 그 변화로 자동검색이 돌면
  //    목록이 다시 열려 「고른 게 저장 안 되고 처음으로 돌아간다」로 보였다(게시판 #58).
  useAutoSearch(term, () => { if (value === null || open) void run(); });

  if (value && !open) {
    return (
      <div className="flex items-center gap-2">
        <div className="field flex-1 truncate bg-canvas">
          {picked || `#${value}`}
          {picked && <span className="ml-2 text-xs text-ink-faint">NO {value}</span>}
        </div>
        <button type="button" className="btn-ghost shrink-0" onClick={() => setOpen(true)}>
          변경
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-1">
      <div className="flex gap-2">
        <input
          className="field flex-1"
          placeholder="검색어"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') search(e);
          }}
        />
        <button type="button" className="btn-ghost" onClick={search} disabled={busy}>
          찾기
        </button>
        {value !== null && (
          <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>
            취소
          </button>
        )}
      </div>
      {hits.length > 0 && (
        <ul className="max-h-40 overflow-y-auto rounded-lg border border-line">
          {hits.map((h, i) => (
            <li key={i}>
              <button
                type="button"
                className="w-full px-3 py-1.5 text-left text-sm hover:bg-canvas"
                onClick={() => {
                  seq.current++;
                  onPick(Number(h[idCol]));
                  setPicked(String(h[nameCol] ?? ''));
                  setOpen(false);
                  setHits([]);
                  setTerm('');
                }}
              >
                {String(h[nameCol] ?? '—')}
                <span className="ml-2 text-xs text-ink-faint">#{String(h[idCol])}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** 목록 행(snake_case)을 폼 값(camelCase)으로. 없는 값은 비운다. */
/** 최초등록일(YYYY-MM-DD) → 차령만료일(+11년 − 1일). 날짜가 아니면 ''. 2/29 등록은 이듬해 규칙대로 2/28 이 된다. */
/** 최초등록일 → 검사만기(+1년 − 1일, #289). 날짜가 아니면 ''. */
export function testExpireOf(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return '';
  const [y, mo, da] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, da));
  if (t.getUTCMonth() !== mo - 1 || t.getUTCDate() !== da) return '';
  const last = new Date(Date.UTC(y + 1, mo, 0)).getUTCDate();
  return new Date(Date.UTC(y + 1, mo - 1, Math.min(da, last)) - 86400000).toISOString().slice(0, 10);
}

export function carExpireOf(s: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s.trim());
  if (!m) return '';
  const [y, mo, da] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const t = new Date(Date.UTC(y, mo - 1, da));
  if (t.getUTCMonth() !== mo - 1 || t.getUTCDate() !== da) return '';
  // +11년: 같은 월·일이 없으면(2/29) 그 달 말일로 — SQL Server dateadd(year) 와 같다
  const last = new Date(Date.UTC(y + 11, mo, 0)).getUTCDate();
  const e = new Date(Date.UTC(y + 11, mo - 1, Math.min(da, last)) - 86400000);
  return e.toISOString().slice(0, 10);
}

function toDraft(kind: MasterKind, row: Row | null): Record<string, unknown> {
  // 새 차량은 운용중으로 시작한다 — 레거시 SP 는 기본이 미운용이라 함정이었다.
  if (!row) return kind === 'car' ? { isUse: true } : {};
  const out: Record<string, unknown> = {};
  for (const f of FIELDS[kind].fields) {
    const v = row[snake(f.key)];
    if (v !== undefined && v !== null) out[f.key] = v;
  }
  const pkCol = snake(FIELDS[kind].pk);
  if (row[pkCol] !== undefined) out[FIELDS[kind].pk] = Number(row[pkCol]);
  return out;
}

/**
 * 관리자·회계 「기사앱으로 보기」 — 그 기사로 로그인된 기사앱을 새 탭에(읽기 전용 · 2시간 · functions `adminViewAsDriver`).
 * 🔴 언제나 driver.busin.co.kr 로 연다 — 담당자 화면과 같은 주소에서 열면 로그인 저장소가 같아 관리자 로그인이 덮인다.
 * 창은 누르는 순간 먼저 연다(팝업 차단) → 토큰을 받으면 주소를 넣는다.
 */
function ViewAsDriverButton({ driverId }: { driverId: number }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const open = async () => {
    setErr(null);
    setBusy(true);
    const w = window.open('about:blank', '_blank');
    try {
      const { token } = await api.adminViewAsDriver({ driverId });
      const url = `https://driver.busin.co.kr/driver#viewas=${encodeURIComponent(token)}`;
      if (w) w.location.href = url; else window.open(url, '_blank');
    } catch (e) {
      w?.close();
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button className="btn-ghost text-xs" onClick={open} disabled={busy}
        title="이 기사로 로그인한 기사앱 화면을 새 탭으로 엽니다 · 읽기 전용(저장 안 됨) · 기사님 폰은 그대로">
        {busy ? '여는 중…' : '기사앱으로 보기'}
      </button>
      {err && <span className="text-xs text-red-600">{err}</span>}
    </>
  );
}

export default function MasterForm({
  kind,
  row,
  onClose,
  onSaved,
  onOpenCust,
  onCreated,
  onOpenExternal,
}: {
  kind: MasterKind;
  row: Row | null;
  onClose: () => void;
  onSaved: (msg: string) => void;
  /** 새 기사·차량·차주에서 「외부」를 고르면 「외부 편성 등록」으로 넘긴다(10-29 way 「소속을 먼저 선택」). */
  onOpenExternal?: () => void;
  /** 중복 거래처 안내에서 기존 거래처를 연다(10-17). */
  onOpenCust?: (custId: number, custName: string) => void;
  /** 새로 등록한 번호를 부른 쪽에 돌려준다(접수 화면 거래처 등록 #354). */
  onCreated?: (id: number, name: string) => void;
}) {
  const def = FIELDS[kind];
  // 서류 쓰기는 기준정보 쓰기와 같은 권한이다(서버도 requireDispatch).
  const { me } = useMe();
  const canEditFiles = canScreen(me, 'master');
  // 조회 권한은 상세·서류를 **보기만** 한다 — 고치기·삭제·서류 올리기는 입력 권한(2026-09-28 way 「조회는 세부까지 보이고 수정은 따로」).
  // 서버도 쓰기는 입력 단계로 막는다(requireScreen 'write') — 여기선 헛누름을 없앨 뿐이다.
  // 단 **새 거래처 등록**은 배차 입력 권한으로도 된다(10-20 #354 · 서버 saveCust 같은 규칙).
  const custCreate = kind === 'cust' && row === null && canScreen(me, 'dispatch');
  const readOnly = !canEditFiles && !custCreate;
  const [d, setD] = useState<Record<string, unknown>>(() => toDraft(kind, row));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const editing = d[def.pk] !== undefined;
  // 「외부 소속만」 수정 권한은 기존 거래처 칸을 못 고친다(서버 saveCust 가 막음 · 10-29) — 칸은 잠그고 담당자 저장·서류만 연다.
  // 예전엔 칸이 열린 채 「수정」이 거절되고 안내가 가리키는 「담당자 저장」 버튼은 안 보였다(10-30 유이현).
  // 「외부 소속만」도 기존 거래처는 전부 고친다(10-30 way · 10-29 칸 잠금을 뒤집음) — 바뀐 칸은 아래 변경기록에 남는다.
  // 새로 등록한 직후 — 창을 닫지 않고 서류를 바로 올리게 한다(2026-09-28 way 「신규등록에 서류 첨부 칸이 없다」).
  // 닫을 때 목록을 다시 불러야 하므로 부모에 줄 문구를 들고 있다.
  const [created, setCreated] = useState<string | null>(null);
  // 등록 전에 고른 서류(#140) — 「등록」으로 번호가 생기면 바로 올린다.
  const [pending, setPending] = useState<Record<string, File>>({});
  // 운행편성 원칙(09-28) — 저장은 됐고 어긋난 점만 알린다. 창을 닫지 않고 보여 준다(닫으면 못 읽는다).
  const [warns, setWarns] = useState<string[]>([]);
  const [carryKey, setCarryKey] = useState(0);
  const close = () => (created ? onSaved(created) : onClose());

  // 외부 양식(10-29 way) — 새 기사·차량·차주는 먼저 내부/외부를 고른다. 외부는 「외부 편성 등록」 한 곳으로 모은다(이름 양식 자동).
  const choosable = row === null && (kind === 'driver' || kind === 'car' || kind === 'owner') && !readOnly;
  const [picked, setPicked] = useState<'내부' | '개인' | '관광사' | null>(null);
  const [extHere, setExtHere] = useState(false);
  const [showAll, setShowAll] = useState(false);
  // 차량은 고른 소속의 구분으로 본다 — 소속 목록은 스무 줄 남짓이라 한 번 읽어 둔다.
  const [grpKinds, setGrpKinds] = useState<Map<number, string>>(new Map());
  useEffect(() => {
    if (kind !== 'car' && kind !== 'car_alloc') return;
    let live = true;
    api.listGrps({ limit: 500 }).then((r) => {
      if (live) setGrpKinds(new Map((r.rows as unknown as Row[]).map((g) => [Number(g.grp_id), String(g.grp_kind ?? '').trim()])));
    }).catch(() => undefined);
    return () => { live = false; };
  }, [kind]);
  const extKind: ExtKind =
    kind === 'owner' ? (d.belong === '외부' ? (d.isPrivate === true ? '개인' : '관광사') : null)
    : kind === 'driver' ? ((row?.ext_kind as '개인' | '관광사' | null | undefined) ?? (picked && picked !== '내부' ? picked : null))
    : kind === 'car' ? (grpKinds.get(Number(d.grpId)) === '외부' || (picked && picked !== '내부' && !d.grpId) ? '외부' : null)
    : kind === 'car_alloc' ? (grpKinds.get(Number(d.grpId)) === '외부' ? '외부' : null)
    : null;
  const extHidden = (f: FieldDef) => !!extKind && !showAll && !!f.extHide && (f.extHide === true || f.extHide === extKind);
  const extHiddenCount = extKind ? def.fields.filter((f) => !f.hidden && !!f.extHide && (f.extHide === true || f.extHide === extKind)).length : 0;
  // 수정 가능 소속이 한쪽뿐이면(10-29 way 「영업부는 외부만 등록 · 외부가 고정으로」) 다른 쪽 선택지를 빼고, 내부만이면 바로 내부.
  const grpScope = me?.masterGrpScope ?? 'all';
  useEffect(() => {
    if (choosable && !picked && grpScope === 'internal') pick('내부');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [choosable, grpScope]);
  // 고르는 중 = 칸·등록 버튼을 아직 안 보인다
  const choosing = choosable && (!picked || (picked !== '내부' && !extHere));
  function pick(p: '내부' | '개인' | '관광사') {
    setPicked(p);
    if (kind === 'owner') setD((cur) => ({ ...cur, belong: p === '내부' ? '내부' : '외부', isPrivate: p === '개인' }));
  }

  // 거래처 담당자 목록(10-08) — 수정이면 서버에서 읽고, 새 거래처는 담당자·정산담당자 빈 줄 하나씩으로 시작한다.
  const contacts = kind === 'cust' ? ((d.contacts as CustContact[] | undefined) ?? null) : null;
  // 기준정보 조회 권한이어도 배차 입력 권한이면 거래처 **담당자만** 고친다(10-14 #235 — 학교는 부서마다 담당이 다르다).
  const contactsOnly = readOnly && kind === 'cust' && editing && canScreen(me, 'dispatch');
  const [contactsMsg, setContactsMsg] = useState<string | null>(null);
  async function saveContactsOnly() {
    setSaving(true); setError(null); setContactsMsg(null);
    try {
      const r = await api.saveCustContacts({ custId: Number(d[def.pk]), contacts: (contacts ?? []) as CustContact[] });
      setD((cur) => ({ ...cur, contacts: r.rows }));
      setContactsMsg('담당자를 저장했습니다.');
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  }
  // 기준정보 조회 권한이어도 회계 입력 권한이면 편성의 **카드 칸만** 고친다(10-19 #335 way 「법인카드 정보만」).
  const cardsOnly = readOnly && kind === 'car_alloc' && editing && canScreen(me, 'accounting');
  const [cardsMsg, setCardsMsg] = useState<string | null>(null);
  async function saveCardsOnly() {
    setSaving(true); setError(null); setCardsMsg(null);
    try {
      await api.saveCarAllocCards({
        carAllocId: Number(d[def.pk]),
        fuelCorp: String(d.fuelCorp ?? ''), fuelNum: String(d.fuelNum ?? ''),
        hipassCorp: String(d.hipassCorp ?? ''), hipassNum: String(d.hipassNum ?? ''),
      });
      setCardsMsg('카드 정보를 저장했습니다.');
      setCarryKey((k) => k + 1); // 저장 이력 다시 읽기
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  }
  // 기준정보 조회 권한이어도 회계 입력 권한이면 기사의 **계좌 칸만** 고친다(10-21 회의 — 회계팀 「기사 계좌번호만」).
  const bankOnly = readOnly && kind === 'driver' && editing && canScreen(me, 'accounting');
  const [bankMsg, setBankMsg] = useState<string | null>(null);
  async function saveBankOnly() {
    setSaving(true); setError(null); setBankMsg(null);
    try {
      await api.saveDriverBank({
        driverId: Number(d[def.pk]),
        bankName: String(d.bankName ?? ''), bankOwner: String(d.bankOwner ?? ''), bankNumber: String(d.bankNumber ?? ''),
      });
      setBankMsg('계좌를 저장했습니다.');
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setSaving(false); }
  }
  const custId = kind === 'cust' && row?.cust_id != null ? Number(row.cust_id) : null;
  useEffect(() => {
    if (kind !== 'cust') return;
    if (!custId) { setD((cur) => (cur.contacts ? cur : { ...cur, contacts: [blankContact('일반담당자'), blankContact('정산담당자')] })); return; }
    let live = true;
    api.listCustContacts({ custId }).then((r) => { if (live) setD((cur) => ({ ...cur, contacts: r.rows })); })
      .catch((e) => { if (live) setError(e instanceof Error ? e.message : String(e)); });
    return () => { live = false; };
  }, [kind, custId]);

  // 중복 거래처 안내(10-17 way 「중복 입력이 안 되게 · 담당이 다르면 기존 거래처에 추가하도록 안내」) —
  // 사업자번호·이름을 치는 동안 이미 있는 거래처를 보여 준다. 저장도 서버가 같은 판정(사업자번호 숫자만)으로 막는다.
  const [dup, setDup] = useState<{ byBiz: CustDupHit[]; byName: CustDupHit[] } | null>(null);
  const bizNow = kind === 'cust' ? String(d.bizNumber ?? '') : '';
  const nameNow = kind === 'cust' ? String(d.custName ?? '').trim() : '';
  const bizOrig = useRef(kind === 'cust' ? String(row?.biz_number ?? '').replace(/\D/g, '') : '');
  useEffect(() => {
    if (kind !== 'cust' || readOnly) return;
    const digits = bizNow.replace(/\D/g, '');
    // 기존 거래처는 번호를 바꿀 때만 본다(레거시 중복끼리 서로 막지 않게 — 서버와 같은 규칙)
    const checkBiz = digits.length >= 10 && digits !== bizOrig.current;
    const checkName = !editing && nameNow.length >= 2;
    if (!checkBiz && !checkName) { setDup(null); return; }
    let live = true;
    const t = setTimeout(() => {
      api.checkCustDup({ custId: editing ? Number(d[def.pk]) : null, bizNumber: checkBiz ? digits : '', custName: checkName ? nameNow : '' })
        .then((r) => { if (live) setDup(r.byBiz.length || r.byName.length ? r : null); })
        .catch(() => { if (live) setDup(null); });
    }, 400);
    return () => { live = false; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, readOnly, bizNow, nameNow, editing]);

  const lastAutoExpire = useRef(carExpireOf(String(d.carDate ?? '')));
  const lastAutoTest = useRef(testExpireOf(String(d.carDate ?? '')));
  const set = (k: string, v: unknown) =>
    setD((cur) => {
      const next = { ...cur, [k]: v };
      // 차령만료일 자동(게시판 #56) — 레거시와 같이 최초등록일 + 11년 − 1일(날짜 둘 다 있는 976대가 전부 이 값).
      // 비어 있거나 직전 최초등록일로 계산한 값일 때만 채운다 — 연장 등으로 손으로 넣은 만료일은 덮지 않는다.
      if (kind === 'car' && k === 'carDate') {
        const auto = carExpireOf(String(v ?? ''));
        const exp = String(cur.carExpire ?? '');
        // 고치는 도중(2026-09-2)을 지나도 판정이 끊기지 않게 마지막 자동값을 기억한다
        if (auto && (!exp || exp === lastAutoExpire.current)) {
          next.carExpire = auto;
          lastAutoExpire.current = auto;
        }
        // 검사만기(#289) — 같은 규칙: 비었거나 직전 자동값일 때만. 손으로 넣은 만기는 그대로.
        const autoT = testExpireOf(String(v ?? ''));
        const te = String(cur.testExpire ?? '');
        if (autoT && (!te || te === lastAutoTest.current)) {
          next.testExpire = autoT;
          lastAutoTest.current = autoT;
        }
      }
      return next;
    });

  // 기사 소속(지급 법인)·소득 구분(09-28 회의) — 차인지급액 조정 설정 표에 따로 있다. 지급처가 바뀌는 값이라 **회계 입력 권한만** 고친다.
  const canPay = kind === 'driver' && canScreen(me, 'accounting');
  const payInit = { grp: row?.pay_grp_id == null ? '' : String(row.pay_grp_id), income: row?.income_kind == null ? '' : String(row.income_kind) };
  const [pay, setPay] = useState(payInit);
  const payDirty = pay.grp !== payInit.grp || pay.income !== payInit.income;

  /** 등록 전에 고른 서류를 올린다. 실패한 것은 이유를 보인다(기준정보는 이미 저장됐다). */
  async function uploadPending(ownerId: number): Promise<string> {
    const owner = FILE_OWNER[kind];
    const list = Object.entries(pending);
    if (!owner || list.length === 0) return '';
    const failed: string[] = [];
    for (const [k, f] of list) {
      try { await uploadFileAsset(owner, ownerId, k, f); } catch (e) { failed.push(e instanceof Error ? e.message : String(e)); }
    }
    setPending({});
    if (failed.length) setError(`서류 ${failed.length}건을 올리지 못했습니다 — ${failed.join(' / ')}`);
    return ` 서류 ${list.length - failed.length}건도 올렸습니다.`;
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      // 외부 → 내부 이전(10-07 way) — 새 편성이 내부 소속이면 같은 연락처의 외부 편성이 끝난다. 저장 전에 알리고 확인을 받는다.
      if (kind === 'car_alloc' && !editing && Number(d.driverId) && Number(d.grpId)) {
        const pv = await api.previewExternalDups({ driverId: Number(d.driverId), grpId: Number(d.grpId), startdt: String(d.startdt ?? '') });
        if (pv.rows.length && !window.confirm(externalDupConfirm(pv))) { setSaving(false); return; }
      }
      const res = (await SAVERS[kind](kind === 'cust' && !Array.isArray(d.contacts) ? { ...d, contacts: undefined } : d)) as Record<string, unknown> | undefined;
      const newId = res?.[def.pk];
      if (canPay && payDirty) {
        const driverId = Number(editing ? d[def.pk] : newId);
        await api.saveToplineDriver({
          driverId,
          payGrpId: pay.grp ? Number(pay.grp) : null,
          incomeKind: (pay.income || null) as '4대보험' | '3.3%' | null,
        });
      }
      const w = Array.isArray(res?.warnings) ? (res!.warnings as string[]) : [];
      const ext = Array.isArray(res?.endedExternal) ? (res!.endedExternal as ExternalDup[]) : [];
      if (ext.length) w.unshift(externalDupDone(ext));
      // 편성 이어받기(#65) — 종료일을 넣어 저장했는데 종료 뒤 항목이 남았으면 창을 닫지 않고 옮길 자리를 보여 준다.
      if (kind === 'car_alloc' && editing && String(d.enddt ?? '') !== '') {
        const pv = await api.previewCarAllocCarry({ carAllocId: Number(d[def.pk]) }).catch(() => null);
        if (pv?.ended && pv.moDefs + pv.moRows + pv.inst + pv.daily > 0) {
          setWarns(w);
          setCreated(`${def.title}을(를) 수정했습니다.`);
          setCarryKey((k) => k + 1);
          setSaving(false);
          return;
        }
      }
      if (w.length) {
        if (!editing && newId !== undefined && newId !== null) {
          await uploadPending(Number(newId));
          set(def.pk, Number(newId));
        }
        setWarns(w);
        setCreated(`${def.title}을(를) ${editing ? '수정' : '등록'}했습니다.`);
        setSaving(false);
        return;
      }
      setWarns([]);
      if (!editing && FILE_OWNER[kind] && newId !== undefined && newId !== null) {
        const up = await uploadPending(Number(newId));
        set(def.pk, Number(newId));
        setCreated(`${def.title}을(를) 등록했습니다.${up}`);
        setSaving(false);
        return;
      }
      if (!editing && newId !== undefined && newId !== null) onCreated?.(Number(newId), String(d.custName ?? ''));
      onSaved(`${def.title}을(를) ${editing ? '수정' : '등록'}했습니다.`);
    } catch (e) {
      // 서버가 이유를 문장으로 준다(중복·참조 등). 그대로 보여준다.
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  /**
   * 차량 복사(#126 · 레거시 「복사」) — 지금 칸을 그대로 두고 새 등록으로 바꾼다.
   * 차량번호·차대번호는 차마다 다르므로 비운다(같은 번호는 서버가 막는다). 서류는 따라가지 않는다.
   */
  function copyAsNew() {
    setD((cur) => {
      // #137 — 같은 차를 새로 등록하는 경우가 있어 차량번호·차대번호도 그대로 둔다(필요하면 고쳐 넣는다).
      const next: Record<string, unknown> = { ...cur, copyFromId: cur[def.pk] };
      delete next[def.pk];
      return next;
    });
    setWarns([]);
    setError(null);
  }

  async function remove() {
    // #126 — 한 번 더 묻는다(삭제는 목록에서 사라진다).
    const name = String(d.carNo ?? d.ownerName ?? d.driverName ?? d.custName ?? d.grpName ?? '');
    if (!window.confirm(`${def.title}${name ? ` 「${name}」` : ''}을(를) 삭제할까요?`)) return;
    setSaving(true);
    setError(null);
    try {
      if (kind === 'car_alloc') await api.deleteCarAlloc({ carAllocId: Number(d[def.pk]) });
      else await api.deleteMaster({ kind, id: Number(d[def.pk]) });
      onSaved(`${def.title}을(를) 삭제했습니다.`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSaving(false);
    }
  }

  return (
    <div className="card space-y-4 p-5">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-base font-bold">
          {def.title} {readOnly ? '상세' : editing ? '수정' : '등록'}
          {editing && d[def.pk] != null && <span className="ml-2 text-sm font-normal text-ink-soft">NO {String(d[def.pk])}</span>}
        </h2>
        {readOnly && <span className="rounded bg-canvas px-2 py-0.5 text-xs text-ink-soft">{cardsOnly ? '조회 권한 — 카드 정보만 고칠 수 있습니다' : bankOnly ? '조회 권한 — 계좌만 고칠 수 있습니다' : '조회 권한 — 보기만 됩니다'}</span>}
        {!readOnly && me?.masterGrpScope && me.masterGrpScope !== 'all' && (
          <span className="rounded bg-amber-50 px-2 py-0.5 text-xs text-amber-800" title="다른 소속 줄은 저장할 때 막힙니다">
            수정 가능: {me.masterGrpScope === 'internal' ? '내부 소속만' : '외부 소속만'}
          </span>
        )}
        {kind === 'driver' && editing && (me?.role === 'admin' || me?.role === 'accounting') && d[def.pk] != null && (
          <ViewAsDriverButton driverId={Number(d[def.pk])} />
        )}
        {kind === 'car' && editing && !readOnly && !created && (
          <button className="btn-ghost text-xs" onClick={copyAsNew} disabled={saving}
                  title="이 차량 정보를 그대로 가져와 새 차량으로 등록합니다(같은 차량번호·차대번호도 됩니다)">
            복사
          </button>
        )}
        {editing && !readOnly && (
          <button className="btn-ghost text-xs text-red-600" onClick={remove} disabled={saving}>
            삭제
          </button>
        )}
        <button className="btn-ghost ml-auto" onClick={close} disabled={saving}>
          {created || readOnly ? '닫기' : '취소'}
        </button>
        {!readOnly && (
          <button className="btn-primary" onClick={save} disabled={saving || choosing}>
            {saving ? '저장 중…' : editing ? '수정' : '등록'}
          </button>
        )}
      </div>

      {def.note && <p className="text-xs text-amber-700">{def.note}</p>}
      {dup && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" role="alert">
          <p className="font-medium">
            {dup.byBiz.length ? '이미 등록된 사업자번호입니다 — 이 번호로는 저장할 수 없습니다.' : '같은 이름의 거래처가 이미 있습니다.'}
          </p>
          <p className="mt-1 text-xs">
            같은 사업장이면 새로 만들지 말고, 기존 거래처를 열어 <b>담당 부서 · 거래처담당자 · 거래처정산담당자</b> 줄을 추가하세요.
          </p>
          <ul className="mt-2 space-y-1">
            {[...dup.byBiz, ...dup.byName.filter((n) => !dup.byBiz.some((b) => b.cust_id === n.cust_id))].map((c) => (
              <li key={c.cust_id} className="flex items-center gap-2">
                <span>「{c.cust_name}」 <span className="text-xs text-ink-soft">거래처 {c.cust_id} · {c.biz_number || '사업자번호 없음'}</span></span>
                {onOpenCust && (
                  <button type="button" className="btn-ghost ml-auto px-2 py-0.5 text-xs" onClick={() => onOpenCust(c.cust_id, c.cust_name ?? '')}>
                    이 거래처 열어 거래처담당자 추가
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <ErrorBox message={error} />}
      {warns.length > 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          <b>저장했습니다.</b> 운행편성 원칙과 다른 점이 있습니다 — 확인해 주세요.
          <ul className="mt-1 list-disc pl-5">{warns.map((m) => <li key={m}>{m}</li>)}</ul>
        </div>
      )}
      {kind === 'car_alloc' && editing && (
        <AllocCarryPanel carAllocId={Number(d[def.pk])} readOnly={readOnly} reloadKey={carryKey} />
      )}
      {kind === 'car_alloc' && editing && <AllocHistoryPanel carAllocId={Number(d[def.pk])} reloadKey={carryKey} />}
      {/* 운행편성 변경(10-02) — 기사·차주·소속이 바뀌거나 끝나는 편성은 「편성 변경」에서 한 번에(옛 편성 종료·새 편성·남은 항목·알림). */}
      {kind === 'car_alloc' && editing && !readOnly && (
        <p className="rounded-lg border border-line bg-canvas px-3 py-2 text-sm text-ink-soft">
          기사·차주·소속이 바뀌거나 편성이 끝나면{' '}
          <Link className="font-semibold text-brand-700 underline" to={`/alloc-changes?from=${Number(d[def.pk])}`}>편성 변경으로 처리</Link>
          하세요. 옛 편성 종료·새 편성 등록·남은 월정기·할부·재무추가 옮기기가 한 번에 되고 회계팀에 알려집니다.
        </p>
      )}
      {created && warns.length === 0 && FILE_OWNER[kind] && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {created} 아래 <b>서류</b> 칸에서 바로 올리세요. 다 올렸으면 「닫기」.
        </div>
      )}

      {choosable && !picked && (
        <div className="space-y-2 rounded-lg border border-line p-4">
          <p className="text-sm font-medium">
            {grpScope === 'external' ? '외부만 등록할 수 있습니다 — 관광사 / 개인을 고르세요.' : '먼저 소속을 고르세요 — 외부는 필요한 칸만 받습니다.'}
          </p>
          <div className="flex flex-wrap gap-2">
            {grpScope !== 'external' && <button type="button" className="btn" onClick={() => pick('내부')}>내부(동영·더모아 등)</button>}
            <button type="button" className="btn" onClick={() => pick('관광사')}>외부 관광사</button>
            <button type="button" className="btn" onClick={() => pick('개인')}>외부 개인</button>
          </div>
        </div>
      )}
      {choosable && picked && picked !== '내부' && !extHere && (
        <div className="space-y-2 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
          <p>
            외부({picked})는 <b>「외부 편성 등록」</b>에서 차주·기사·차량·편성을 한 번에 만듭니다 — 이름이 양식(「{picked}_이름」)대로 맞춰지고
            같은 {picked === '관광사' ? '사업자번호' : '계좌'}의 차주가 있으면 그 차주를 다시 씁니다.
          </p>
          <div className="flex flex-wrap gap-2">
            {onOpenExternal && <button type="button" className="btn-primary" onClick={onOpenExternal}>외부 편성 등록 열기</button>}
            <button type="button" className="btn-ghost" onClick={() => setExtHere(true)}>{def.title}만 여기서 등록</button>
            <button type="button" className="btn-ghost" onClick={() => { setPicked(null); setExtHere(false); }}>다시 고르기</button>
          </div>
        </div>
      )}
      {extKind && extHiddenCount > 0 && !choosing && (
        <p className="flex flex-wrap items-center gap-2 rounded-lg bg-canvas px-3 py-2 text-xs text-ink-soft">
          외부{extKind === '외부' ? '' : ` ${extKind}`} 양식 — 외부에선 쓰지 않는 칸 {extHiddenCount}개를 {showAll ? '펼쳐 보이는 중입니다' : '숨겼습니다(값은 그대로 남습니다)'}.
          <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={() => setShowAll((v) => !v)}>
            {showAll ? '다시 숨기기' : '숨긴 칸 보기'}
          </button>
        </p>
      )}

      {!choosing && (() => {
        const byKey = new Map(def.fields.map((f) => [f.key, f] as const));
        const listed = new Set((def.sections ?? []).flatMap((x) => x.keys ?? []));
        const rest = def.fields.filter((f) => !f.hidden && !listed.has(f.key));
        // 묶음에 안 적힌 칸은 마지막 본문 묶음 뒤에 붙인다(새 칸을 빠뜨려도 화면에서 사라지지 않게)
        const lastMain = (def.sections ?? []).reduce((a, y, j) => (!y.side && y.keys ? j : a), -1);
        const sections: Section[] = def.sections
          ? def.sections.map((x, i) => (i === lastMain ? { ...x, keys: [...(x.keys ?? []), ...rest.map((f) => f.key)] } : x))
          : [{ keys: def.fields.filter((f) => !f.hidden).map((f) => f.key) }];
        const hasFiles = sections.some((x) => x.files);

        // 서류 — 등록 전에도 고를 수 있다(#140). 고른 파일은 「등록」 뒤 uploadPending 이 올린다.
        const files = FILE_OWNER[kind] && (
          editing
            ? <FileAssets ownerType={FILE_OWNER[kind]!} ownerId={Number(d[def.pk])} canEdit={canEditFiles} />
            : <FileAssets ownerType={FILE_OWNER[kind]!} ownerId={null} canEdit={canEditFiles} pending={pending}
                          onPending={(k, f) => setPending((cur) => { const n = { ...cur }; if (f) n[k] = f; else delete n[k]; return n; })} />
        );

        const field = (key: string) => {
          if (key === '_contacts') {
            return kind === 'cust' ? (
              <div key={key} className="md:col-span-12">
                <ContactsField rows={contacts} loading={contacts === null} onChange={(rows) => setD((cur) => ({ ...cur, contacts: rows }))} />
                {contactsOnly && (
                  <div className="mt-2 flex items-center gap-2">
                    <button type="button" className="btn-primary py-1 text-sm" disabled={saving || contacts === null} onClick={() => void saveContactsOnly()}>
                      {saving ? '저장 중…' : '담당자 저장'}
                    </button>
                    <span className="text-xs text-ink-soft">{contactsMsg ?? '담당자만 저장됩니다 — 거래처명·사업자번호 등은 기준정보 입력 권한이 고칩니다'}</span>
                  </div>
                )}
              </div>
            ) : null;
          }
          if (key === '_rrn') {
            // 외부 관광사 기사는 주민번호가 필요 없다(청구 · 3.3% 원천징수는 개인만)
            return kind === 'driver' && !(extKind === '관광사' && !showAll) ? (
              <div key={key} className="md:col-span-12">
                <RrnField
                  driverId={editing ? Number(d[def.pk]) : null}
                  hint={row?.res_no_hint == null ? null : String(row.res_no_hint)}
                  readOnly={readOnly}
                  canReveal={me?.rrnReveal ?? can(me?.role, 'accounting')}
                  value={String(d.resNo ?? '')}
                  cleared={d.resNoClear === true}
                  onChange={(v) => setD((cur) => ({ ...cur, resNo: v, resNoClear: false }))}
                  onClear={() => setD((cur) => ({ ...cur, resNo: '', resNoClear: true }))}
                />
              </div>
            ) : null;
          }
          const f = byKey.get(key);
          if (!f || f.hidden || extHidden(f)) return null;
          // #462 개인 차주 사업자번호 칸 = 가린 주민번호 → 「전체 보기」 버튼이 들어가므로 label 대신 div(label 이 클릭을 되쏜다 · issues 103)
          const ownerRrn = kind === 'owner' && f.key === 'bizNumber' && editing && String(d.bizNumber ?? '').includes('*');
          const Wrap = ownerRrn ? 'div' : 'label';
          return (
            <Fragment key={f.key}>
              <Wrap className={`block ${SPAN[f.wide ? 12 : f.span ?? 4]}`}>
                <span className="mb-1 block text-sm font-medium">
                  {f.label}
                  {f.required && !(f.extOptional && extKind) && <span className="ml-0.5 text-red-600">*</span>}
                  {f.extOptional && extKind && <span className="ml-2 text-xs font-normal text-ink-faint">외부는 비우면 기간 없이</span>}
                  {f.hint && <span className="ml-2 text-xs font-normal text-ink-faint">{f.hint}</span>}
                </span>
                {f.type === 'ref' ? (
                  <RefPicker
                    kind={f.ref!}
                    value={d[f.key] === undefined || d[f.key] === null ? null : Number(d[f.key])}
                    label={String((f.labelCol ? row?.[f.labelCol] : row?.[refLabelCol(f.ref!)] ?? row?.[snake(f.key).replace(/_id$/, '_name')]) ?? '')}
                    onPick={(id) => set(f.key, id)}
                  />
                ) : f.type === 'select' ? (
                  <select className="field" value={String(d[f.key] ?? '')} onChange={(e) => set(f.key, e.target.value)}>
                    {(f.options ?? []).map((o) => (
                      <option key={o} value={o}>
                        {o === '' ? '선택 안 함' : kindLabel(o)}
                      </option>
                    ))}
                  </select>
                ) : f.type === 'checkbox' ? (
                  <input
                    type="checkbox"
                    className="mt-2"
                    checked={d[f.key] === true}
                    onChange={(e) => set(f.key, e.target.checked)}
                  />
                ) : kind === 'car' && f.key === 'carCode' ? (
                  <CarCodeField value={String(d.carCode ?? '')} onChange={(v) => set('carCode', v)}
                                onPick={(code, name) => setD((cur) => ({ ...cur, carCode: code, carName: name }))} />
                ) : f.type === 'textarea' ? (
                  <textarea
                    className={`field ${f.key === 'history' ? 'h-40' : 'h-24'}`}
                    value={String(d[f.key] ?? '')}
                    placeholder={d.belong === '외부' ? f.extPh : undefined}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                ) : f.type === 'money' ? (
                  <input
                    className="field text-right"
                    inputMode="numeric"
                    value={moneyView(d[f.key])}
                    onChange={(e) => set(f.key, e.target.value.replace(/[^0-9.-]/g, ''))}
                  />
                ) : (
                  <input
                    className="field"
                    type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date'
                      : f.type === 'dateText' && /^(\d{4}-\d{2}-\d{2})?$/.test(String(d[f.key] ?? '').trim()) ? 'date' : 'text'}
                    value={String(d[f.key] ?? '')}
                    placeholder={d.belong === '외부' ? f.extPh : undefined}
                    onChange={(e) => set(f.key, f.type === 'number' ? Number(e.target.value) : e.target.value)}
                  />
                )}
                {ownerRrn && (me?.rrnReveal ?? can(me?.role, 'accounting')) && (
                  <OwnerRrnReveal ownerId={Number(d[def.pk])} />
                )}
              </Wrap>
              {/* 급여 지급 설정은 급여·급여구분 곁에 둔다 — 맨 아래(상담메모 뒤)에 두니 「활성화 안 돼 있네?」로 읽혔다(09-29 way). */}
              {kind === 'driver' && f.key === PAY_AFTER && (
                <div className="md:col-span-12">
                  <PayField
                    value={pay}
                    grpLabel={row?.pay_grp_name == null ? '' : String(row.pay_grp_name)}
                    readOnly={readOnly || !canPay}
                    onChange={setPay}
                  />
                </div>
              )}
            </Fragment>
          );
        };

        const box = (x: Section, i: number) => (
          <section key={i} className={x.title ? 'space-y-3 rounded-lg border border-line p-3' : 'space-y-3'}>
            {x.title && <h3 className="text-sm font-bold">{x.title}</h3>}
            {x.files && files}
            {x.keys && x.keys.length > 0 && (
              <fieldset disabled={readOnly} className="grid gap-4 md:grid-cols-12">
                {(contactsOnly ? x.keys.filter((k) => k !== '_contacts')
                  : cardsOnly ? x.keys.filter((k) => !CARD_KEYS.includes(k))
                  : bankOnly ? x.keys.filter((k) => !BANK_KEYS.includes(k)) : x.keys).map(field)}
              </fieldset>
            )}
            {/* 계좌만 고칠 수 있는 회계팀(10-21)도 잠긴 칸 밖에 따로 그린다 */}
            {bankOnly && x.keys?.some((k) => BANK_KEYS.includes(k)) && (
              <div className="space-y-2 rounded-lg border border-line p-3">
                <div className="grid gap-4 md:grid-cols-12">{x.keys.filter((k) => BANK_KEYS.includes(k)).map(field)}</div>
                <div className="flex items-center gap-2">
                  <button type="button" className="btn-primary py-1 text-sm" disabled={saving} onClick={() => void saveBankOnly()}>
                    {saving ? '저장 중…' : '계좌 저장'}
                  </button>
                  <span className="text-xs text-ink-soft">{bankMsg ?? '은행·예금주·계좌번호만 저장됩니다'}</span>
                </div>
              </div>
            )}
            {/* 카드만 고칠 수 있는 사람(#335)도 잠긴 칸 밖에 따로 그린다 */}
            {cardsOnly && x.keys?.some((k) => CARD_KEYS.includes(k)) && (
              <div className="space-y-2 rounded-lg border border-line p-3">
                <div className="grid gap-4 md:grid-cols-12">{x.keys.filter((k) => CARD_KEYS.includes(k)).map(field)}</div>
                <div className="flex items-center gap-2">
                  <button type="button" className="btn-primary py-1 text-sm" disabled={saving} onClick={() => void saveCardsOnly()}>
                    {saving ? '저장 중…' : '카드 정보 저장'}
                  </button>
                  <span className="text-xs text-ink-soft">{cardsMsg ?? '주유카드·하이패스만 저장됩니다'}</span>
                </div>
              </div>
            )}
            {/* 담당자만 고칠 수 있는 사람(#235)은 잠긴 칸 밖에 그린다 — disabled fieldset 안쪽은 다시 못 켠다 */}
            {contactsOnly && x.keys?.includes('_contacts') && field('_contacts')}
          </section>
        );
        const main = sections.filter((x) => !x.side);
        const side = sections.filter((x) => x.side);
        return (
          <>
            {side.length > 0 ? (
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
                <div className="space-y-4">{main.map(box)}</div>
                <div className="space-y-4">{side.map((x, i) => box(x, 100 + i))}</div>
              </div>
            ) : (
              <div className="space-y-4">{main.map(box)}</div>
            )}
            {kind === 'driver' && !listed.has('_rrn') && field('_rrn')}
            {files && !hasFiles && <div className="border-t border-line pt-4">{files}</div>}
          </>
        );
      })()}
      {kind === 'car' && editing && <CarChangePanel carId={Number(d[def.pk])} />}
      {kind === 'cust' && editing && <CarChangePanel custId={Number(d[def.pk])} />}
    </div>
  );
}

/**
 * 기사 소속(급여가 나가는 법인)·소득 구분(2026-09-28 회의 「기사 등록 시부터 입력」).
 * 저장처는 `topline_driver_setting`(차인지급액 조정과 같은 행) — 거기서 고쳐도 여기 보이고, 여기서 고쳐도 거기 반영된다.
 * 비우면 기본값: 소속 = 그 달 편성 소속, 소득 = 4대보험(떼지 않음). 소득 구분은 지금 **예비기사 지급**에만 쓰인다.
 */
function PayField({ value, grpLabel, readOnly, onChange }: {
  value: { grp: string; income: string }; grpLabel: string; readOnly: boolean;
  onChange: (v: { grp: string; income: string }) => void;
}) {
  const [grps, setGrps] = useState<{ grp_id: number; grp_name: string }[] | null>(null);
  useEffect(() => {
    if (readOnly) return;
    api.listGrps({ limit: 200 })
      .then((r) => setGrps((r.rows as { grp_id: number; grp_name: string }[]).map((g) => ({ grp_id: Number(g.grp_id), grp_name: g.grp_name }))))
      .catch(() => setGrps([]));
  }, [readOnly]);
  return (
    <div className="grid gap-4 border-t border-line pt-4 md:grid-cols-3">
      <div className="md:col-span-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">급여 지급 설정</span>
        <span className="text-xs text-ink-faint">
          차인지급액 조정에 쓰입니다{readOnly ? ' · 회계 입력 권한이 있어야 고칩니다' : ''}
        </span>
      </div>
      {readOnly ? (
        <>
          <div className="text-sm"><span className="mb-1 block text-xs text-ink-soft">기사 소속(급여가 나가는 법인)</span>{grpLabel || '편성 소속 그대로'}</div>
          <div className="text-sm"><span className="mb-1 block text-xs text-ink-soft">소득 구분</span>{value.income || '4대보험(기본)'}</div>
        </>
      ) : (
        <>
          <label className="block">
            <span className="mb-1 block text-xs text-ink-soft">기사 소속(급여가 나가는 법인) — 편성 소속과 다를 때만</span>
            <select className="field" value={value.grp} onChange={(e) => onChange({ ...value, grp: e.target.value })}>
              <option value="">편성 소속 그대로</option>
              {/* 목록이 오기 전에도 지금 값은 보이게 */}
              {grps === null && value.grp && <option value={value.grp}>{grpLabel || value.grp}</option>}
              {(grps ?? []).map((g) => <option key={g.grp_id} value={String(g.grp_id)}>{g.grp_name}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-ink-soft">소득 구분</span>
            <select className="field" value={value.income} onChange={(e) => onChange({ ...value, income: e.target.value })}>
              <option value="">기본(4대보험)</option>
              <option value="4대보험">4대보험 — 떼지 않음</option>
              <option value="3.3%">3.3% — 사업소득, 떼고 지급</option>
            </select>
          </label>
        </>
      )}
    </div>
  );
}

/**
 * 기사 주민번호(2026-09-28 way 「정산 시 필요」). 서버는 암호화해서만 둔다(functions/src/rrn.ts).
 * 화면엔 **가린 값만** 온다 — 평문은 폼이 모르므로 빈 칸으로 저장해도 기존 값은 그대로다(바꿀 때만 입력).
 * 「전체 보기」는 인차팀·회계·관리자만(#185), 볼 때마다 서버에 기록된다.
 */
function RrnField({ driverId, hint, readOnly, canReveal, value, cleared, onChange, onClear }: {
  driverId: number | null; hint: string | null; readOnly: boolean; canReveal: boolean;
  value: string; cleared: boolean; onChange: (v: string) => void; onClear: () => void;
}) {
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function reveal() {
    if (!driverId) return;
    setBusy(true); setErr(null);
    try { setShown((await api.revealDriverRrn({ driverId })).resNo); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  const current = cleared ? '지움(저장하면 반영)' : shown ?? hint ?? '없음';
  return (
    <div className="grid gap-2 border-t border-line pt-4 md:grid-cols-3">
      <div className="md:col-span-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium">주민번호</span>
        <span className="text-xs text-ink-faint">외부 개인기사 3.3% 정산에 필요합니다 · 암호화해 저장합니다</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 md:col-span-3">
        <span className="rounded bg-canvas px-2 py-1 font-mono text-sm">{current}</span>
        {canReveal && driverId && hint && !shown && !cleared && (
          <button type="button" className="btn-ghost text-xs" onClick={reveal} disabled={busy}>
            {busy ? '여는 중…' : '전체 보기'}
          </button>
        )}
        {shown && <span className="text-xs text-ink-faint">열람 기록이 남습니다</span>}
        {!readOnly && hint && !cleared && (
          <button type="button" className="btn-ghost text-xs text-red-600" onClick={onClear}>지우기</button>
        )}
      </div>
      {!readOnly && (
        <label className="block">
          <span className="mb-1 block text-xs text-ink-soft">{hint ? '바꿀 때만 입력' : '입력'}</span>
          <input
            className="field font-mono"
            inputMode="numeric"
            autoComplete="off"
            placeholder="900101-1234567"
            maxLength={14}
            value={value}
            onChange={(e) => onChange(e.target.value)}
          />
        </label>
      )}
      {err && <div className="md:col-span-3"><ErrorBox message={err} /></div>}
    </div>
  );
}

/** 개인 차주 주민번호 전체 보기(#462) — 칸 값은 가린 그대로 두고 옆에만 보인다(저장 값과 무관). 볼 때마다 서버에 기록. */
function OwnerRrnReveal({ ownerId }: { ownerId: number }) {
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function reveal() {
    setBusy(true); setErr(null);
    try { setShown((await api.revealOwnerRrn({ ownerId })).resNo); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2">
      {shown ? (
        <>
          <span className="rounded bg-canvas px-2 py-1 font-mono text-sm">{shown}</span>
          <span className="text-xs text-ink-faint">열람 기록이 남습니다</span>
        </>
      ) : (
        <button type="button" className="btn-ghost text-xs" onClick={() => void reveal()} disabled={busy}>
          {busy ? '여는 중…' : '주민번호 전체 보기'}
        </button>
      )}
      {err && <span className="text-xs text-red-600">{err}</span>}
    </div>
  );
}

const blankContact = (kind: CustContact['kind']): CustContact => ({ id: null, kind, dept: '', name: '', phone: '', email: '' });

/**
 * 담당자·정산담당자 여러 명(10-08 way 「여러 개를 등록해서 추후 선택해서 정산」) — 레거시 `cust_manager`.
 * 종류별 **첫 줄 = 기본**(목록·계산서 기본 이메일에 쓰인다). 「기본으로」는 그 줄을 같은 종류 맨 앞으로 옮긴다.
 * 계산서 발행요청에서는 이메일이 있는 사람을 골라 이메일·참조자 칸에 넣는다.
 */
function ContactsField({ rows, loading, onChange }: { rows: CustContact[] | null; loading: boolean; onChange: (rows: CustContact[]) => void }) {
  if (loading || !rows) return <p className="text-sm text-ink-faint">담당자 불러오는 중…</p>;
  const set = (i: number, k: 'dept' | 'name' | 'phone' | 'email', v: string) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const remove = (i: number) => onChange(rows.filter((_, j) => j !== i));
  const makeMain = (i: number) => {
    const r = rows[i]!;
    const rest = rows.filter((_, j) => j !== i);
    const at = rest.findIndex((x) => x.kind === r.kind);
    onChange(at < 0 ? [...rest, r] : [...rest.slice(0, at), r, ...rest.slice(at)]);
  };
  const group = (kind: CustContact['kind']) => {
    const list = rows.map((r, i) => ({ r, i })).filter((x) => x.r.kind === kind);
    return (
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">{CONTACT_KIND_LABEL[kind]}</span>
          <span className="text-xs text-ink-faint">{CONTACT_KIND_HINT[kind]} · 맨 위가 기본 · 부서마다 다르면 줄을 추가하세요</span>
          <button type="button" className="btn-ghost ml-auto py-0.5 text-xs" onClick={() => onChange([...rows, blankContact(kind)])}>+ 추가</button>
        </div>
        {list.length === 0 && <p className="text-xs text-ink-faint">없음</p>}
        {list.map(({ r, i }, n) => (
          <div key={i} className="grid grid-cols-12 items-center gap-2">
            <span className={`col-span-12 text-xs sm:col-span-1 ${n === 0 ? 'font-semibold text-brand-700' : 'text-ink-faint'}`}>{n === 0 ? '기본' : n + 1}</span>
            <input className="field col-span-6 sm:col-span-2" placeholder="부서" aria-label="담당 부서" value={r.dept ?? ''} onChange={(e) => set(i, 'dept', e.target.value)} />
            <input className="field col-span-6 sm:col-span-2" placeholder="이름" value={r.name} onChange={(e) => set(i, 'name', e.target.value)} />
            <input className="field col-span-6 sm:col-span-2" placeholder="연락처" value={r.phone} onChange={(e) => set(i, 'phone', e.target.value)} />
            <input className="field col-span-6 sm:col-span-3" placeholder="이메일" type="email" value={r.email} onChange={(e) => set(i, 'email', e.target.value.trim())} />
            <div className="col-span-12 flex gap-1 sm:col-span-2">
              {n > 0 && <button type="button" className="btn-ghost px-2 py-0.5 text-xs" onClick={() => makeMain(i)}>기본으로</button>}
              <button type="button" className="btn-ghost px-2 py-0.5 text-xs text-red-600" onClick={() => remove(i)}>빼기</button>
            </div>
          </div>
        ))}
      </div>
    );
  };
  return (
    <div className="space-y-3 rounded-lg border border-line p-3">
      {group('일반담당자')}
      {group('정산담당자')}
    </div>
  );
}

/** 형식코드(#289) — 구 버스인 「형식코드조회」. 표에서 고르면 형식코드와 차량명이 같이 채워진다(차량명 통일). 손으로 쳐도 된다. */
function CarCodeField({ value, onChange, onPick }: { value: string; onChange: (v: string) => void; onPick: (code: string, name: string) => void }) {
  const [codes, setCodes] = useState<{ code: string; car_name: string }[] | null>(null);
  const [open, setOpen] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function show() {
    setOpen((o) => !o);
    if (codes) return;
    try { setCodes((await api.listCarCodes({})).rows); } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  const q = value.trim().toLowerCase();
  const list = (codes ?? []).filter((c) => !q || c.code.toLowerCase().includes(q) || c.car_name.toLowerCase().includes(q));
  return (
    <div className="relative">
      <div className="flex items-center gap-2">
        <input className="field flex-1" value={value} onChange={(e) => onChange(e.target.value)} />
        <button type="button" className="btn-ghost shrink-0" onClick={() => void show()}>조회</button>
      </div>
      {open && (
        <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-auto rounded-lg border border-line bg-surface text-sm shadow-lg">
          {err && <li className="px-3 py-2 text-red-700">{err}</li>}
          {!codes && !err && <li className="px-3 py-2 text-ink-faint">불러오는 중…</li>}
          {codes && list.length === 0 && <li className="px-3 py-2 text-ink-faint">맞는 형식코드가 없습니다.</li>}
          {list.map((c) => (
            <li key={c.code}>
              <button type="button" className="w-full px-3 py-1.5 text-left hover:bg-brand-50"
                      onClick={() => { onPick(c.code, c.car_name); setOpen(false); }}>
                <b className="mr-2">{c.code}</b>{c.car_name}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
